import gsap from 'gsap';
import { createContext, useContext, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

const initialState = {
    debugBallTrail: false,
    debugBlocked: false,
    debugBoardMeter: false,
    debugBounceGates: false,
    debugBudget: false,
    debugClosedGates: false,
    debugContentVariables: false,
    debugCostSpinner: false,
    debugElementEditor: false,
    debugElementNames: false,
    debugGateExpectedProfits: false,
    debugGateMeter: false,
    debugGateValues: false,
    debugHints: false,
    debugImpact: false,
    debugMeterValues: false,
    debugMetricMeter: false,
    debugProgress: false,
    debugResetButton: false,
    metricMax: null,
    metricName: null,
    speed: 1,
    volume: 0.2,
};

const SettingsContext = createContext<SettingsState>({} as SettingsState);

export const useSettings = () => useContext(SettingsContext);

export function withSettings(WrappedComponent: any) {
    return function SettingsWrappedComponent(props: any) {
        return (
            <SettingsContext.Consumer>
                {state => <WrappedComponent {...props} settings={state} />}
            </SettingsContext.Consumer>
        );
    };
}

export function SettingsProvider({ children }: SettingsProviderProps) {
    const [ready, setReady] = useState(false);
    const [state, setState] = useLocalStorage<Partial<SettingsState>>('pl.settings', initialState);

    const reloaders: string[] = [];

    useEffect(() => {
        if (!ready) {
            setReady(true);

            // Check if keys match
            const initialKeys = Object.keys(initialState);
            const stateKeys = Object.keys(state);
            if (
                !initialKeys.every(key => stateKeys.includes(key)) ||
                !stateKeys.every(key => initialKeys.includes(key))
            ) {
                setState(initialState);
            }
        }
    }, [ready, state, setState]);

    useEffect(() => {
        // Update GSAP global speed
        gsap.globalTimeline.timeScale(state?.speed || 1);
    }, [state.speed]);

    function set(name: string, value: any) {
        // Update state
        setState({
            ...initialState,
            ...state,
            [name]: value,
        });

        // Reload page needed
        if (reloaders.includes(name)) {
            window.location.reload();
        }
    }

    const stateValue = {
        ...(process.env.NEXT_PUBLIC_ADMIN ? state : initialState),
        reloaders,
        set,
    } as SettingsState;

    return <SettingsContext.Provider value={stateValue}>{children}</SettingsContext.Provider>;
}
